import React from "react";
import PatientRecords from "../patientRecords/PatientRecords";
import usePatientListSelection from "./hook/usePatientListSelection";
import { Autocomplete, CircularProgress, TextField } from "@mui/material";
import SideDrawerWrapper from "../side-drawer/SideDrawerWrapper/SideDrawerWrapper";
import ConfirmModal from "components/common/modals/confirmModal/ConfirmModal";
import FormTextField from "components/FormFields/FormTextField";

const PatientListSelection = ({
  title,
  subTitle,
  description,
  showSearchField,
  successButtonText = "",
  cancelButtonText = "Cancel",

  handleClose,
  showDrawer = false,

  patientList = [],
  isProcessing,
  handleConfirm,
  confirmModalText,
  searchable = false,
  excludePatch = false,
}) => {
  const {
    isLoading,
    inputValue,
    patientRecords,
    showConfirmation,
    selectedPatientRecordEvokeId,
    searchResults,
    hasSearched,

    setInputValue,
    handlePatientFilter,
    setShowConfirmation,
    handlePatientConfirm,
    handlePatientEvokeIdEntry,
    handlePatientRecordSelect,
    handleClearPatientRecords,
    handleRemovePatient,
  } = usePatientListSelection(
    patientList,
    handleConfirm,
    handleClose,
    excludePatch
  );

  const [isPatientListOpen, setIsPatientListOpen] = React.useState(false);

  React.useEffect(() => {
    if (
      inputValue.length >= 3 &&
      (searchResults.length > 0 || (hasSearched && !isLoading))
    ) {
      setIsPatientListOpen(true);
    } else {
      setIsPatientListOpen(false);
    }
  }, [isLoading, inputValue, searchResults, hasSearched]);

  return (
    <SideDrawerWrapper
      title={title}
      showDrawer={showDrawer}
      handleClose={handleClose}
      cancelButtonText={cancelButtonText}
      successButtonText={successButtonText}
      handleCancel={handleClearPatientRecords}
      disableConfirm={
        !selectedPatientRecordEvokeId || patientRecords.length < 2
      }
      disableCancel={!patientRecords.length}
      handleSubmit={() => setShowConfirmation(true)}
    >
      <>
        {showSearchField && (
          <Autocomplete
            freeSolo={searchResults.length > 0 || isLoading}
            fullWidth
            open={isPatientListOpen}
            onClose={() => setIsPatientListOpen(false)}
            filterSelectedOptions
            selectOnFocus
            handleHomeEndKeys
            disableClearable={true}
            style={{ marginTop: "20px", marginBottom: "8px" }}
            componentsProps={{
              popper: {
                style: { zIndex: 1302 },
                modifiers: [
                  {
                    name: "sameWidth",
                    enabled: true,
                    phase: "beforeWrite",
                    fn: ({ state }) => {
                      state.styles.popper.width = `${state.rects.reference.width}px`;
                    },
                  },
                ],
              },
            }}
            noOptionsText={"No patients found."}
            inputValue={inputValue}
            options={searchResults}
            renderOption={(props, option) => {
              const { key, ...optionProps } = props;
              const {
                dob = "",
                deviceInformation = {},
                evoke_id = "",
                account = "",
              } = option;
              const { modelNumber = "" } = deviceInformation;

              return (
                <div
                  key={key}
                  style={{
                    padding: "5px 10px",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "flex-start",
                    alignItems: "flex-start",
                  }}
                  {...optionProps}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    <span>{option.name}</span>
                    {evoke_id && (
                      <span
                        style={{
                          fontSize: "12px",
                          color: "#666",
                          fontWeight: "normal",
                        }}
                      >
                        ({evoke_id})
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 12 }}>
                    <b style={{ color: "#00000099" }}>
                      DOB: {dob} | Serial Number: {modelNumber}
                      {account && ` | Account: ${account}`}
                    </b>
                  </div>
                </div>
              );
            }}
            onChange={(event, selectedValue) => {
              if (selectedValue && selectedValue.evoke_id) {
                handlePatientEvokeIdEntry([selectedValue.evoke_id]);
                setInputValue("");
                setIsPatientListOpen(false);
              }
            }}
            onInputChange={(event, newValue, reason) => {
              if (reason === "input") {
                setInputValue(newValue);
                if (!newValue || newValue.length < 3) {
                  setIsPatientListOpen(false);
                }
              }
            }}
            getOptionLabel={(option) => {
              return option?.name || option;
            }}
            filterOptions={(options, { inputValue }) => {
              if (/[0-9/]/.test(inputValue)) {
                return options;
              }
              return options.filter((option) =>
                option.name?.toLowerCase().includes(inputValue.toLowerCase())
              );
            }}
            renderInput={(params) => (
              <div style={{ position: "relative" }}>
                <TextField
                  {...params}
                  required={false}
                  label="Search Patient by Name or DOB (MM/DD/YYYY) or Evoke ID"
                />

                {isLoading && (
                  <CircularProgress
                    size={20}
                    style={{
                      position: "absolute",
                      top: "40%",
                      right: 18,
                      color: "#94BA33",
                    }}
                  />
                )}
              </div>
            )}
          />
        )}

        {searchable && (
          <FormTextField
            className="mb-2"
            fullWidth={true}
            placeholder="Search patient"
            onChange={(e) => handlePatientFilter(e.target.value)}
          />
        )}

        {patientRecords.length > 0 && (
          <>
            <p className="mt-2">
              <h3>{subTitle}</h3>
              <p>{description}</p>
            </p>

            <PatientRecords
              patientRecords={patientRecords}
              value={selectedPatientRecordEvokeId}
              handleSelect={handlePatientRecordSelect}
              handleRemove={handleRemovePatient}
            />
          </>
        )}
      </>
      {showConfirmation && (
        <ConfirmModal
          title=""
          confirmText="Yes"
          isConfirming={isProcessing}
          show={showConfirmation}
          onConfirm={handlePatientConfirm}
          onCancel={() => setShowConfirmation(false)}
          children={confirmModalText}
          style={{ zIndex: 1301 }}
        />
      )}
    </SideDrawerWrapper>
  );
};

export default PatientListSelection;
